import { apiClient } from './api';

export type DiscountType = 'percent' | 'fixed';
export type PromoScope = 'order' | 'product' | 'category';

export interface PromoCode {
  PromoID: string;
  Code: string;
  Description: string | null;
  DiscountType: DiscountType;
  DiscountValue: number;
  Scope: PromoScope;
  IsPublic: boolean;
  MinOrderAmount: number | null;
  PerCustomerLimit: number | null;
  TotalUseLimit: number | null;
  StartDate: string | null;
  EndDate: string | null;
  IsActive: boolean;
  CreatedAt: string;
  UpdatedAt: string;
  CreatedBy: string | null;
  customerIds: string[];
  scopeTargets: string[];
  redemptionCount: number;
}

export interface PromoCodeInput {
  code: string;
  description?: string | null;
  discountType: DiscountType;
  discountValue: number;
  scope: PromoScope;
  isPublic: boolean;
  minOrderAmount?: number | null;
  perCustomerLimit?: number | null;
  totalUseLimit?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  isActive?: boolean;
  customerIds?: string[];
  scopeTargets?: string[];
}

/** Server-authoritative result of previewing a code against a cart. */
export interface PromoPricingResult {
  valid: boolean;
  message: string;
  code?: string;
  promoId?: string;
  discountType?: DiscountType;
  discountValue?: number;
  discountAmt: number;
  eligibleSubtotal: number;
}

export interface PromoValidateCartItem {
  SupersedeNo: string;
  Qty: number;
  Price: number;
}

class AdminPromoCodeService {
  async list(): Promise<PromoCode[]> {
    const r = await apiClient.get<PromoCode[]>('/admin/promo-codes');
    return r.data ?? [];
  }

  async get(promoId: string): Promise<PromoCode> {
    const r = await apiClient.get<PromoCode>(`/admin/promo-codes/${promoId}`);
    return r.data!;
  }

  async create(input: PromoCodeInput): Promise<PromoCode> {
    const r = await apiClient.post<PromoCode>('/admin/promo-codes', input);
    return r.data!;
  }

  async update(promoId: string, input: PromoCodeInput): Promise<PromoCode> {
    const r = await apiClient.put<PromoCode>(`/admin/promo-codes/${promoId}`, input);
    return r.data!;
  }

  async setActive(promoId: string, isActive: boolean): Promise<void> {
    await apiClient.patch(`/admin/promo-codes/${promoId}/active`, { isActive });
  }
}

class PromoCodeService {
  /**
   * Preview a code against the cart. Returns the server-authoritative discount;
   * `valid` is false (with a `message`) when the code can't be applied.
   */
  async validate(code: string, cartItems: PromoValidateCartItem[]): Promise<PromoPricingResult> {
    const r = await apiClient.post<PromoPricingResult>('/promo/validate', { code, cartItems });
    // Backend always 200s with the result in `data`, even when invalid.
    return (
      r.data ?? { valid: false, message: r.message || 'Failed to validate promo code', discountAmt: 0, eligibleSubtotal: 0 }
    );
  }
}

export const adminPromoCodeService = new AdminPromoCodeService();
export const promoCodeService = new PromoCodeService();
